import React, { useEffect, useRef, useState } from "react";
import { Box, Typography } from "@mui/material";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import IconReusable from "../../../../components/IconReusable";

const ACCEPT = ".jpg,.jpeg,.png,.pdf";

const AadhaarUploadBox = ({ label, file, onSelect, onDelete, error }) => {
  const inputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);

  const isPdf = file?.type === "application/pdf";

  useEffect(() => {
    if (file && !isPdf) {
      const url = URL.createObjectURL(file);
      setPreviewUrl(url);
      return () => URL.revokeObjectURL(url);
    }
    setPreviewUrl(null);
  }, [file, isPdf]);

  const pick = (f) => f && onSelect(f);

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    pick(e.dataTransfer.files?.[0]);
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 0.5,
        flex: 1,
        minWidth: 0,
      }}>
      <Typography variant="labelM" sx={{ color: "#666666" }}>
        {label}
      </Typography>

      {file ? (
        <>
          <Box
            sx={{
              display: { xs: "flex", md: "none" },
              alignItems: "center",
              gap: 2,
              pl: 0,
              pr: 1,
              py: 1,
              borderRadius: "4px",
              width: "100%",
            }}>
            {isPdf ? (
              <Box
                sx={{
                  width: 65,
                  height: 41,
                  flexShrink: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}>
                <PictureAsPdfIcon sx={{ fontSize: 32, color: "#CC0000" }} />
              </Box>
            ) : (
              <Box
                component="img"
                src={previewUrl}
                alt={file.name}
                sx={{
                  width: 65,
                  height: 41,
                  flexShrink: 0,
                  objectFit: "cover",
                  borderRadius: "4px",
                }}
              />
            )}
            <Typography
              variant="p1"
              sx={{ flex: 1, minWidth: 0, wordBreak: "break-word" }}>
              {file.name}
            </Typography>
            <Box
              sx={{ cursor: "pointer", display: "flex", p: 0.5 }}
              onClick={onDelete}>
              <IconReusable name="Delete" color="#995A00" />
            </Box>
          </Box>

          <Box
            sx={{
              display: { xs: "none", md: "flex" },
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 1,
              minHeight: "151px",
              borderRadius: "8px",
              width: "100%",
              p: 2,
            }}>
            {isPdf ? (
              <PictureAsPdfIcon sx={{ fontSize: 40, color: "#CC0000" }} />
            ) : (
              <Box
                component="img"
                src={previewUrl}
                alt={file.name}
                sx={{
                  maxWidth: "80px",
                  maxHeight: "48px",
                  objectFit: "contain",
                  borderRadius: "4px",
                }}
              />
            )}
            <Typography
              variant="p3"
              sx={{
                color: "#8C8C8C",
                textAlign: "center",
                wordBreak: "break-word",
              }}>
              {file.name}
            </Typography>
            <Box sx={{ cursor: "pointer", display: "flex" }} onClick={onDelete}>
              <IconReusable name="Delete" color="#995A00" />
            </Box>
          </Box>
        </>
      ) : (
        <Box
          onClick={() => inputRef.current?.click()}
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 0.5,
            px: 2,
            py: 4,
            width: "100%",
            minHeight: "151px",
            borderRadius: "8px",
            border: "1px solid #D9D9D9",
            backgroundColor: dragOver ? "#F0F0F0" : "#F7F7F7",
            cursor: "pointer",
          }}>
          <IconReusable name="Upload" color="#666666" />
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Typography
              variant="body1"
              sx={{
                color: "#333",
              }}>
              Drag &amp; drop file or&nbsp;
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: "#0081F2",
              }}>
              Browse
            </Typography>
          </Box>
          <Typography
            variant="p3"
            sx={{
              color: "#8C8C8C",
              textAlign: "center",
            }}>
            PNG, JPG or Pdf (max. 3MB)
          </Typography>
        </Box>
      )}

      {error && (
        <Typography variant="p3" sx={{ color: "#CC0000", mt: 0.5 }}>
          {error}
        </Typography>
      )}

      <input
        ref={inputRef}
        type="file"
        accept={ACCEPT}
        hidden
        onChange={(e) => {
          pick(e.target.files?.[0]);
          e.target.value = "";
        }}
      />
    </Box>
  );
};

export default AadhaarUploadBox;
